import { api } from '@goaltree/backend/convex/_generated/api'
import type { Focus } from '@goaltree/backend/convex/lib/focus'
import { MAX_OWN_TASKS, cleanOwnTasks } from '@goaltree/backend/convex/lib/ownTasks'
import { Link, createFileRoute, useLocation, useNavigate, useRouter } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { X } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TemplateLanes } from '@/components/discover-templates'
import GoalInput from '@/components/goal-input'
import LifeCoachFlow from '@/components/life-coach-flow'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import {
  DraftsPanel,
  GoalIdeas,
  GoalStepPills,
  TryChips,
  useGoalDraft,
} from '@/components/new-goal-sections'
import { FocusStep } from '@/components/onboarding-focus'
import { OnboardingHeader } from '@/components/onboarding-progress'
import { SiteHeader } from '@/components/site-header'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'
import { useOnboarding } from '@/hooks/use-onboarding'
import { useOnboardingMode } from '@/hooks/use-onboarding-mode'
import { useReadCaptureImage } from '@/hooks/use-photo-tasks'
import { useSignedIn } from '@/hooks/use-signed-in'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { authClient } from '@/lib/auth-client'
import { handleUsageLimit } from '@/lib/usage-limit'
import { useOnboardingStore } from '@/store/onboarding.store'

export const Route = createFileRoute('/goals/new/')({
  validateSearch: (search: Record<string, unknown>): { goal?: string } => ({
    goal: typeof search.goal === 'string' ? search.goal : undefined,
  }),
  head: ({ match }) => {
    const goal = match.search.goal
    return {
      meta: [
        {
          property: 'og:title',
          content: goal ? `${goal} — Start this goal on Goaltree` : 'Create a new goal — Goaltree',
        },
        {
          property: 'og:description',
          content:
            'AI breaks your goal into milestones, weekly targets, and daily tasks — then keeps you accountable.',
        },
        {
          property: 'og:image',
          content: goal
            ? `https://goaltree.ai/og/goal?title=${encodeURIComponent(goal)}`
            : 'https://goaltree.ai/og.png?v=4',
        },
        { name: 'twitter:card', content: 'summary_large_image' },
      ],
    }
  },
  component: RouteComponent,
})

const FREE_GOAL_LIMIT = 3

type Mode = 'input' | 'coach'

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const location = useLocation()
  const store = useOnboardingStore()
  const { isAuthenticated } = useConvexAuth()
  const signedIn = useSignedIn()
  const { surpriseMe, getLifeCoachSuggestions, isLoading, error } = useOnboarding()
  const { isPro, isLoading: isProLoading, trialEligible } = useIsPro()
  const startTrial = useMutation(api.subscriptions.startMyProTrial)
  const goals = useGoals()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  // Signed-in but never onboarded (e.g. Google from the landing page): this is
  // step 1 of their onboarding, so let them skip the goal.
  const firstRun =
    !!prefs &&
    !(prefs as { onboardingCompletedAt?: number }).onboardingCompletedAt &&
    goals !== undefined &&
    !goals.some((g) => !g.isInbox)
  const onboarding = useOnboardingMode()
  // Returning users (signed in, with a goal) get "What's next" and ideas; everyone else the steps.
  const returning =
    signedIn === 'in' && !onboarding.active && !firstRun && !!goals?.some((g) => !g.isInbox)
  const { data: session } = authClient.useSession()
  const currentUser = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const firstName = (session?.user?.name || currentUser?.name)?.trim().split(/\s+/)[0]
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<'goal-limit' | 'coach' | 'voice'>('goal-limit')
  const ownTasks = useOnboardingStore((s) => s.ownTasks)
  const readImage = useReadCaptureImage()
  const [photoReading, setPhotoReading] = useState(false)
  const [mode, setMode] = useState<Mode>('input')
  const [coachProgress, setCoachProgress] = useState(1 / 3)
  const [surpriseMeLoading, setSurpriseMeLoading] = useState(false)
  const [fill, setFill] = useState<{ text: string; n: number }>()
  const scrollRef = useRef<HTMLDivElement>(null)
  const draft = useGoalDraft()
  const [category, setCategory] = useState<string | null>(null)
  const { goal: goalParam } = Route.useSearch()
  const posthog = usePostHog()
  const deepLinkFiredRef = useRef(false)
  const goalInputStartedRef = useRef(false)
  const urlUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const updatePrefs = useUpdatePreferences()
  const [repick, setRepick] = useState(false)
  // A habits pick that lands back here (e.g. from the landing page) is offered the choice again.
  const pickingFocus =
    onboarding.active &&
    !returning &&
    !goalParam &&
    mode === 'input' &&
    (repick || !onboarding.focus || onboarding.focus === 'habits')

  function pickFocus(focus: Focus) {
    posthog.capture('onboarding_focus_selected', { focus })
    store.setFocus(focus)
    setRepick(false)
    if (signedIn === 'in') void updatePrefs({ focus }).catch(() => {})
    if (focus !== 'habits') return
    if (signedIn === 'in') void navigate({ to: '/onboarding/user-profile' })
    else void navigate({ to: '/auth/login', search: { returnTo: '/onboarding/user-profile' } })
  }

  // goaltree.ai can't always see the app session (another browser, or a phone's home-screen app),
  // so its "Get started" can land an onboarded user here; Today is where they belong (coaching
  // mode forwards on from there).
  useEffect(() => {
    if (goalParam || !isAuthenticated || prefs == null || goals === undefined || firstRun) return
    if (!/^https:\/\/(www\.)?goaltree\.ai\//.test(document.referrer)) return
    void navigate({ to: '/today', replace: true })
  }, [goalParam, isAuthenticated, prefs, goals, firstRun, navigate])

  function handleGoalValueChange(value: string) {
    if (!goalInputStartedRef.current && value.length > 0) {
      goalInputStartedRef.current = true
      posthog.capture('goal_input_started')
    }
    deepLinkFiredRef.current = true
    if (urlUpdateTimerRef.current) clearTimeout(urlUpdateTimerRef.current)
    urlUpdateTimerRef.current = setTimeout(() => {
      urlUpdateTimerRef.current = null
      // Submitting within the debounce already left this step; navigating to
      // /goals/new now would bounce the user back from the questions.
      if (router.state.location.pathname.replace(/\/$/, '') !== '/goals/new') return
      const trimmed = value.trim()
      navigate({ to: '/goals/new', search: trimmed ? { goal: trimmed } : undefined, replace: true })
    }, 200)
  }

  function cancelUrlUpdate() {
    if (urlUpdateTimerRef.current) clearTimeout(urlUpdateTimerRef.current)
    urlUpdateTimerRef.current = null
  }

  useEffect(() => cancelUrlUpdate, [])

  useEffect(() => {
    const autoSubmit = (location.state as unknown as Record<string, unknown> | null)?.autoSubmit
    if (goalParam && !isProLoading && !deepLinkFiredRef.current && autoSubmit) {
      deepLinkFiredRef.current = true
      posthog.capture('goal_prefilled_from_url', { goal: goalParam })
      handleGoalSubmit(goalParam)
    }
  }, [goalParam, isProLoading])

  function checkGoalLimit(): boolean {
    if (!isProLoading && !isPro && goals !== undefined && goals.length >= FREE_GOAL_LIMIT) {
      setUpgradeReason('goal-limit')
      setUpgradeOpen(true)
      return false
    }
    return true
  }

  async function handleCoachMe() {
    if (!isProLoading && !isPro) {
      // Coach me is AI-metered per user, so signed-out visitors sign up first.
      if (signedIn === 'out') {
        navigate({ to: '/auth/login', search: { returnTo: '/goals/new' } })
        return
      }
      // New users get the trial for free anyway; start it here instead of
      // putting a paywall in front of someone mid-onboarding.
      const started = trialEligible ? await startTrial({}).catch(() => null) : null
      if (!started?.started) {
        setUpgradeReason('coach')
        setUpgradeOpen(true)
        return
      }
      posthog.capture('pro_trial_started', { reason: 'coach-auto' })
      toast.success(t('trial.started'))
    }
    navigate({ to: '/goals/new', replace: true })
    setMode('coach')
  }

  async function handleGoalSubmit(goalText: string, category?: string) {
    if (!checkGoalLimit()) return
    cancelUrlUpdate()
    deepLinkFiredRef.current = true
    posthog.capture('goal_decomposition_requested', { goal: goalText, category })
    store.setGoalText(goalText)
    store.setCategory(category ? category[0].toUpperCase() + category.slice(1) : null)
    store.setPlanAnswers({})
    store.setGeneratedTree(null)
    store.setTemplateId(null)
    await navigate({ to: '/goals/new/questions' })
  }

  async function handleSurpriseMe() {
    if (!checkGoalLimit()) return
    setSurpriseMeLoading(true)
    const goalText = await surpriseMe()
    setSurpriseMeLoading(false)
    if (goalText) {
      await handleGoalSubmit(goalText)
    }
  }

  async function handlePhoto(file: File) {
    setPhotoReading(true)
    try {
      const { items } = await readImage(file)
      const found = items.filter((i) => i.kind === 'task' || i.kind === 'event').map((i) => i.title)
      if (!found.length) {
        toast(t('voice.imageNoItems'))
        return
      }
      const current = useOnboardingStore.getState().ownTasks
      const next = cleanOwnTasks([...current, ...found])
      store.setOwnTasks(next)
      store.setGeneratedTree(null)
      posthog.capture('goal_photo_tasks', { count: next.length - current.length })
      if (current.length + found.length > MAX_OWN_TASKS)
        toast(t('photoTasks.capped', { count: MAX_OWN_TASKS }))
    } catch (err) {
      if (!handleUsageLimit(err)) toast.error(t('voice.imageError'))
    } finally {
      setPhotoReading(false)
    }
  }

  function removeOwnTask(index: number) {
    store.setOwnTasks(ownTasks.filter((_, i) => i !== index))
    store.setGeneratedTree(null)
  }

  function fillComposer(text: string) {
    setFill((f) => ({ text, n: (f?.n ?? 0) + 1 }))
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function resumeDraft() {
    cancelUrlUpdate()
    deepLinkFiredRef.current = true
    void navigate({ to: draft?.kind === 'plan' ? '/goals/new/preview' : '/goals/new/questions' })
  }

  function handleShareGoal(goalText: string) {
    const url = `https://goaltree.ai/g?goal=${encodeURIComponent(goalText).replace(/%20/g, '+')}`
    navigator.clipboard.writeText(url)
    toast.success(t('planner.suggest.shareGoalCopied'))
  }

  const composer = (
    <>
      <GoalInput
        category={category}
        onCategoryChange={setCategory}
        onSubmit={handleGoalSubmit}
        isLoading={isLoading || surpriseMeLoading}
        onSurpriseMe={handleSurpriseMe}
        onCoachMe={handleCoachMe}
        surpriseMeLoading={surpriseMeLoading}
        prefillValue={goalParam}
        fill={fill}
        submitLabel={t('planner.buildPlan')}
        onValueChange={handleGoalValueChange}
        onShareGoal={handleShareGoal}
        onPhoto={signedIn === 'in' ? (file) => void handlePhoto(file) : undefined}
        onPhotoLocked={
          isPro || isProLoading
            ? undefined
            : () => {
                setUpgradeReason('voice')
                setUpgradeOpen(true)
              }
        }
        photoReading={photoReading}
      />
      {!returning && <TryChips onPick={fillComposer} />}
      {ownTasks.length > 0 && (
        <section className="w-full rounded-2xl border border-border/60 bg-card/40 px-4 py-3 backdrop-blur-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('photoTasks.yourTasks', { count: ownTasks.length })}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('photoTasks.yourTasksHint')}</p>
          <ul className="mt-2">
            {ownTasks.map((title, i) => (
              <li
                key={title}
                className="flex items-center gap-2 border-b border-border/30 py-1 last:border-b-0"
              >
                <span className="size-1.5 shrink-0 rounded-full bg-primary" />
                <span className="min-w-0 flex-1 truncate text-sm">{title}</span>
                <button
                  type="button"
                  onClick={() => removeOwnTask(i)}
                  aria-label={t('photoTasks.remove', { title })}
                  title={t('photoTasks.remove', { title })}
                  className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {signedIn === 'out' && (
        <p className="text-center text-xs text-muted-foreground">{t('onboarding.flow.freeNote')}</p>
      )}

      {firstRun && (
        <Link
          to="/onboarding/user-profile"
          className="self-center text-xs text-muted-foreground/70 underline-offset-4 hover:text-foreground hover:underline"
        >
          {t('onboarding.flow.skipGoal')}
        </Link>
      )}

      {error && (
        <div className="space-y-2 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => handleGoalSubmit(store.goalText, store.category?.toLowerCase())}
            disabled={isLoading}
            className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            {t('common.retry')}
          </button>
        </div>
      )}
    </>
  )

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Full-bleed ambient backdrop (blobs + grid), behind the header too */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none">
        <div className="absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-primary/[0.07] blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-[#2dd4bf]/[0.06] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[300px] w-[300px] rounded-full bg-[#3DA9FC]/[0.05] blur-[100px]" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      {onboarding.active ? (
        <OnboardingHeader
          step={pickingFocus ? 1 : onboarding.step}
          partial={mode === 'coach' ? coachProgress : undefined}
          onBack={pickingFocus || mode !== 'input' ? undefined : () => setRepick(true)}
        />
      ) : (
        <>
          <MobileScreenHeader
            title={t('nav.newGoal')}
            showInbox={false}
            showSearch={false}
            onClose={
              !isAuthenticated
                ? () => {
                    if (router.history.length > 1) router.history.back()
                    else navigate({ to: '/' })
                  }
                : undefined
            }
          />
          <SiteHeader className="hidden lg:flex" />
        </>
      )}
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={upgradeReason} />
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          ref={scrollRef}
          className={`relative mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 lg:px-6 ${onboarding.active ? '' : 'pb-16 lg:pb-0'}`}
        >
          <div
            className={`relative flex w-full flex-1 flex-col ${mode === 'input' ? 'gap-10 pt-6 pb-12 lg:pt-12' : 'pt-6 pb-2'}`}
          >
            {pickingFocus ? (
              <FocusStep selected={repick ? onboarding.focus : null} onPick={pickFocus} />
            ) : mode === 'coach' ? (
              <div className="mx-auto w-full max-w-xl">
                <LifeCoachFlow
                  inOnboarding={onboarding.active}
                  onProgress={setCoachProgress}
                  onBack={() => setMode('input')}
                  onGoalSelected={handleGoalSubmit}
                  getSuggestions={getLifeCoachSuggestions}
                  isLoading={isLoading}
                />
              </div>
            ) : (
              <>
                {returning ? (
                  <div className="flex flex-wrap items-start gap-6">
                    <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-3.5">
                      <div className="flex flex-col gap-1.5">
                        <h1 className="text-[30px] leading-[1.08] font-black tracking-tight text-balance sm:text-[38px]">
                          {firstName
                            ? t('planner.whatsNext', { name: firstName })
                            : t('planner.whatsNextAnon')}
                        </h1>
                        <p className="text-[15px] text-muted-foreground">
                          {t('planner.nextSubtitle')}
                        </p>
                      </div>
                      {composer}
                    </div>
                    {draft && <DraftsPanel draft={draft} onResume={resumeDraft} />}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-6 sm:gap-8">
                    <div className="flex flex-col items-center gap-3.5 text-center">
                      {firstRun && firstName && (
                        <p className="text-sm text-muted-foreground">
                          {t('onboarding.flow.welcome', { name: firstName })}
                        </p>
                      )}
                      <GoalStepPills />
                      <h1 className="text-[32px] leading-[1.05] font-black tracking-tight text-balance sm:text-[44px]">
                        {t('planner.headlinePre')}{' '}
                        <span className="bg-gradient-to-r from-primary-text to-primary bg-clip-text text-transparent dark:from-primary dark:to-[color-mix(in_oklab,var(--primary)_50%,white)]">
                          {t('planner.headlineGradient')}
                        </span>
                      </h1>
                      <p className="max-w-xl text-[15px] text-balance text-muted-foreground">
                        {t('planner.subtitleDeadline')}
                      </p>
                    </div>
                    <div className="flex w-full max-w-3xl flex-col gap-3">{composer}</div>
                  </div>
                )}

                {returning && <GoalIdeas onPick={fillComposer} />}

                {!firstRun && (
                  <TemplateLanes
                    onSelect={isAuthenticated ? undefined : (tpl) => fillComposer(tpl.title)}
                    seeAll={isAuthenticated}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
